import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FileText, Eye, Check, X, Clock, AlertCircle, Download, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface MentorW9 {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  w9_status: string;
  w9_submitted_at: string | null;
  w9_approved_at: string | null;
  w9_file_url: string | null;
}

export const AdminW9Manager: React.FC = () => {
  const [mentors, setMentors] = useState<MentorW9[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMentor, setSelectedMentor] = useState<MentorW9 | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [updating, setUpdating] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchMentors();
    
    // Set up real-time subscription to mentor_compliance changes
    const subscription = supabase
      .channel('mentor_compliance_changes')
      .on('postgres_changes', 
          { event: '*', schema: 'public', table: 'mentor_compliance' }, 
          (payload) => {
            console.log('W-9 compliance data changed:', payload);
            fetchMentors(); // Refresh data when compliance changes
          })
      .subscribe();
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchMentors = async () => {
    try {
      setLoading(true);
      console.log('Fetching mentor W-9 data...');
      
      // Clear any cached data by forcing a fresh request
      const timestamp = Date.now();
      console.log(`Fetch timestamp: ${timestamp}`);
      
      const { data, error } = await supabase
        .from('mentors')
        .select(`
          id, 
          first_name, 
          last_name, 
          email,
          mentor_compliance (
            w9_status,
            w9_submitted_at,
            w9_approved_at,
            w9_file_url
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      console.log('Raw mentor data:', JSON.stringify(data, null, 2));
      
      // Transform the data to flatten the compliance fields
      const transformedData = data?.map(mentor => {
        console.log(`Processing mentor: ${mentor.first_name} ${mentor.last_name}`);
        console.log(`Compliance data:`, mentor.mentor_compliance);
        
        // mentor_compliance is an object, not an array!
        const compliance = mentor.mentor_compliance as any || {};
        const w9Status = compliance.w9_status || 'pending';
        
        console.log(`Final W9 status for ${mentor.first_name}: ${w9Status}`);
        
        return {
          id: mentor.id,
          first_name: mentor.first_name,
          last_name: mentor.last_name,
          email: mentor.email,
          w9_status: w9Status,
          w9_submitted_at: compliance.w9_submitted_at || null,
          w9_approved_at: compliance.w9_approved_at || null,
          w9_file_url: compliance.w9_file_url || null,
        };
      }) || [];
      
      console.log('Transformed mentor data:', JSON.stringify(transformedData, null, 2));
      setMentors(transformedData);
    } catch (error) {
      console.error('Error fetching mentors:', error);
      toast({
        title: "Error",
        description: "Failed to load mentor W-9 data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    await fetchMentors();
    toast({
      title: "Data refreshed",
      description: "Loaded latest W-9 submissions.",
    });
  };

  const downloadW9 = async (mentor: MentorW9) => {
    if (!mentor.w9_file_url) return;
    
    try {
      console.log('Attempting to download W-9 for:', mentor.first_name, mentor.last_name);
      console.log('File path:', mentor.w9_file_url);
      
      // Generate a signed URL for private bucket access
      const { data, error } = await supabase.storage
        .from('w9-documents')
        .createSignedUrl(mentor.w9_file_url, 300); // 5 minute expiry
      
      console.log('Signed URL response:', { data, error });
      
      if (error) {
        console.error('Error creating signed URL:', error);
        toast({
          title: "Error",
          description: `Failed to access W-9 file: ${error.message}`,
          variant: "destructive",
        });
        return;
      }
      
      if (!data?.signedUrl) {
        console.error('No signed URL returned');
        toast({
          title: "Error",
          description: "Failed to generate download link.",
          variant: "destructive",
        });
        return;
      }
      
      console.log('Generated signed URL:', data.signedUrl);
      
      // Fetch the file using the signed URL
      const response = await fetch(data.signedUrl);
      console.log('Fetch response status:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      console.log('File blob size:', blob.size, 'type:', blob.type);
      
      // Generate filename: W9_FirstName_LastName_YYYY-MM-DD
      const submissionDate = mentor.w9_submitted_at 
        ? new Date(mentor.w9_submitted_at).toISOString().split('T')[0]
        : 'unknown-date';
      
      const fileExtension = mentor.w9_file_url.split('.').pop() || 'pdf';
      const filename = `W9_${mentor.first_name}_${mentor.last_name}_${submissionDate}.${fileExtension}`;
      
      console.log('Download filename:', filename);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Success",
        description: `Downloaded ${filename}`,
      });
    } catch (error) {
      console.error('Error downloading W-9:', error);
      toast({
        title: "Error",
        description: `Failed to download W-9 file: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const updateW9Status = async (mentorId: string, status: 'approved' | 'rejected') => {
    try {
      setUpdating(true);
      console.log('Updating W9 status:', { mentorId, status });
      
      const updateData: any = {
        w9_status: status,
        updated_at: new Date().toISOString(),
      };

      if (status === 'approved') {
        updateData.w9_approved_at = new Date().toISOString();
      }

      console.log('Update data:', updateData);

      const { data, error, count } = await supabase
        .from('mentor_compliance')
        .update(updateData)
        .eq('mentor_id', mentorId)
        .select();

      console.log('Update response:', { data, error, count });

      if (error) {
        console.error('Update error details:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.error('No rows updated - possible RLS policy issue');
        throw new Error('Failed to update W9 status - no rows affected');
      }

      // Send notification to mentor
      const mentor = mentors.find(m => m.id === mentorId);
      if (mentor) {
        const { error: notifError } = await supabase
          .from('notifications')
          .insert({
            user_id: (await supabase.from('mentors').select('user_id').eq('id', mentorId).single()).data?.user_id,
            title: `W-9 ${status === 'approved' ? 'Approved' : 'Rejected'}`,
            message: status === 'approved' 
              ? 'Your W-9 form has been approved. You can now create invoices for completed sessions.'
              : `Your W-9 form was rejected${rejectionReason ? `: ${rejectionReason}` : '. Please resubmit with corrections.'}`,
            type: status === 'approved' ? 'success' : 'warning'
          });

        if (notifError) {
          console.error('Error sending notification:', notifError);
        }
      }

      toast({
        title: "Success",
        description: `W-9 ${status} successfully.`,
      });

      setSelectedMentor(null);
      setRejectionReason('');
      fetchMentors();
    } catch (error) {
      console.error('Error updating W-9 status:', error);
      toast({
        title: "Error",
        description: "Failed to update W-9 status.",
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <X className="h-4 w-4 text-red-500" />;
      case 'submitted':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'submitted':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const pendingCount = mentors.filter(m => m.w9_status === 'submitted').length;
  const approvedCount = mentors.filter(m => m.w9_status === 'approved').length;
  const rejectedCount = mentors.filter(m => m.w9_status === 'rejected').length;
  const notSubmittedCount = mentors.filter(m => m.w9_status === 'pending').length;

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{pendingCount}</div>
            <div className="text-sm text-gray-600">Pending Review</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{approvedCount}</div>
            <div className="text-sm text-gray-600">Approved</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{rejectedCount}</div>
            <div className="text-sm text-gray-600">Rejected</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-600">{notSubmittedCount}</div>
            <div className="text-sm text-gray-600">Not Submitted</div>
          </CardContent>
        </Card>
      </div>

      {/* W-9 List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Mentor W-9 Forms</CardTitle>
              <CardDescription>
                Review and manage mentor W-9 tax forms
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={refreshData}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {mentors.map((mentor) => (
              <div key={mentor.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="font-medium">
                    {mentor.first_name} {mentor.last_name}
                  </div>
                  <div className="text-sm text-gray-600">{mentor.email}</div>
                  {mentor.w9_submitted_at && (
                    <div className="text-xs text-gray-500">
                      Submitted: {new Date(mentor.w9_submitted_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-3">
                  <Badge className={getStatusColor(mentor.w9_status)}>
                    {getStatusIcon(mentor.w9_status)}
                    <span className="ml-1 capitalize">{mentor.w9_status}</span>
                  </Badge>
                  
                  {mentor.w9_file_url && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadW9(mentor)}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                      
                      {mentor.w9_status === 'submitted' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => updateW9Status(mentor.id, 'approved')}
                            disabled={updating}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedMentor(mentor)}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Review & Reject
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogHeader>
                                <DialogTitle>
                                  W-9 Form - {mentor.first_name} {mentor.last_name}
                                </DialogTitle>
                                <DialogDescription>
                                  Review the submitted W-9 form and provide rejection feedback.
                                </DialogDescription>
                              </DialogHeader>
                              
                              <div className="space-y-4">
                                <div className="border rounded-lg p-4 bg-gray-50">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium">W-9 Document</span>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={async () => {
                                        try {
                                          const { data, error } = await supabase.storage
                                            .from('w9-documents')
                                            .createSignedUrl(mentor.w9_file_url!, 300);
                                          
                                          if (error) throw error;
                                          if (data?.signedUrl) {
                                            window.open(data.signedUrl, '_blank');
                                          }
                                        } catch (error) {
                                          toast({
                                            title: "Error",
                                            description: "Failed to open document",
                                            variant: "destructive",
                                          });
                                        }
                                      }}
                                    >
                                      <FileText className="h-4 w-4 mr-1" />
                                      View Document
                                    </Button>
                                  </div>
                                </div>

                                <div className="space-y-4">
                                  <div>
                                    <Label htmlFor="rejection-reason">
                                      Rejection Reason (required for rejection)
                                    </Label>
                                    <Textarea
                                      id="rejection-reason"
                                      placeholder="Enter specific reason for rejection..."
                                      value={rejectionReason}
                                      onChange={(e) => setRejectionReason(e.target.value)}
                                    />
                                  </div>
                                  
                                  <div className="flex gap-2">
                                    <Button
                                      variant="destructive"
                                      onClick={() => updateW9Status(mentor.id, 'rejected')}
                                      disabled={updating || !rejectionReason.trim()}
                                    >
                                      <X className="h-4 w-4 mr-1" />
                                      Reject
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};